import {type PressEvent} from '@react-types/shared';
import {IconButton} from '@wandb/components';
import React, {useCallback, useMemo, useState} from 'react';

import {
  NotebookPermission,
  useNotebookPermissionsQuery,
} from '../../generated/graphql';
import {trackMarimoClickRun} from '../../services/analytics/productEvents';
import type * as PanelTypes from '../../state/views/panel/types';
import {usePanelSharedProps} from '../Panel/hooks/usePanelSharedProps';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {
  LOADING_STRINGS,
  useOnClickRunCodeButtonInPanel,
} from '../WorkspaceDrawer/Settings/marimo/useOnCreateNotebookClick';
import {usePanelNotebookID} from './notebookIDSelector';
import {MarimoMode, useMarimoState} from './store';

function PanelRunButtonInner({
  notebookId,
  buttonSize,
}: {
  notebookId: string;
  buttonSize: 'sm' | 'md';
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingString, setLoadingString] = useState(LOADING_STRINGS[0]);
  const onClick = useOnClickRunCodeButtonInPanel(
    notebookId,
    setIsLoading,
    setLoadingString
  );

  const tooltip = useMemo(
    () => ({children: isLoading ? loadingString : 'Run code'}),
    [isLoading, loadingString]
  );
  const onPress = useCallback(
    async (e: PressEvent) => {
      e.continuePropagation();
      trackMarimoClickRun({notebookId, source: 'header'});
      await onClick();
    },
    [onClick, notebookId]
  );

  return (
    <IconButton
      icon={isLoading ? 'loading' : 'play'}
      variant="ghost"
      aria-label={tooltip.children}
      size={buttonSize}
      tooltip={tooltip}
      onPress={onPress}
    />
  );
}

export function PanelRunButton({
  panelRef,
  buttonSize,
}: {
  panelRef: PanelTypes.Ref;
  buttonSize: 'sm' | 'md';
}) {
  const panelId = useStablePanelId();
  const {mode} = useMarimoState(state => state.getPanelState(panelId));
  const notebookId = usePanelNotebookID(panelRef);
  const {data} = useNotebookPermissionsQuery({
    variables: {id: notebookId ?? ''},
    skip: !notebookId,
  });

  if (
    !notebookId ||
    mode === MarimoMode.RUN ||
    !data?.notebook?.viewerPermissions.includes(NotebookPermission.Run)
  ) {
    return null;
  }

  return (
    <PanelRunButtonInner notebookId={notebookId} buttonSize={buttonSize} />
  );
}
